import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Plus, BookOpen, Star, Users, TrendingUp, LogOut, Users2, Bell, BellOff, KeyRound, Gift, Church, MessageCircle, BarChart3, PenLine, Share2, Headphones, Calendar, ChevronRight, Sparkles, X, CheckCircle, Circle, Video, Play } from "lucide-react";
import AddChildModal from "./AddChildModal";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useLanguage } from "../hooks/useLanguage";
import LanguageToggle from "./LanguageToggle";
import DiscussionCards from "./DiscussionCards";

// Import refactored components
import {
  ChildCard,
  ChangePasswordModal,
  SetPrizeModal,
  PrayerRequestModal,
  FamilyDevotionalHistory,
  AvatarDisplay,
  AVATARS
} from "./parent";

// Re-export for backward compatibility
export { AvatarDisplay, AVATARS };

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editChild, setEditChild] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(null);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [todaysThoughts, setTodaysThoughts] = useState([]);
  const [childrenRewards, setChildrenRewards] = useState([]);
  const [familyStudyHistory, setFamilyStudyHistory] = useState([]);
  const [curriculumWeek, setCurriculumWeek] = useState(null);
  const [familyLessonDay, setFamilyLessonDay] = useState(null);
  const [showLessonDayPicker, setShowLessonDayPicker] = useState(false);
  const { isSupported, isSubscribed, subscribe, unsubscribe, loading: pushLoading } = usePushNotifications();

  const STAT_CARDS = [
    { label: t('children_stat') || 'Children', icon: Users, gradFrom: "#118AB2", gradTo: "#0d6e91" },
    { label: t('todays_lessons_stat') || "Today's Lessons", icon: BookOpen, gradFrom: "#06D6A0", gradTo: "#049a73" },
    { label: t('total_lessons_stat') || 'Total Lessons', icon: TrendingUp, gradFrom: "#9B5DE5", gradTo: "#7a3fc3" },
    { label: t('stars_earned_stat') || 'Stars Earned', icon: Star, gradFrom: "#FFD166", gradTo: "#F77F00" },
  ];

  const handleToggleNotifications = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      const result = await subscribe();
      if (!result.success) {
        alert(result.error === 'Permission denied' 
          ? t('allow_notifications') || 'Please allow notifications in your browser settings.' 
          : t('could_not_enable_notifications') || 'Could not enable notifications.');
      }
    }
  };

  const loadChildren = async () => {
    try {
      const { data } = await axios.get(`${API}/progress/parent/summary`);
      setChildren(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  
  const loadPrayerRequests = async () => {
    if (!user?.church_id) return;
    try {
      const { data } = await axios.get(`${API}/prayer-requests`);
      setPrayerRequests(data);
    } catch (e) { console.error(e); }
  };
  
  const loadJournalEntries = async () => {
    try {
      // Get ALL journal entries and thoughts
      const { data } = await axios.get(`${API}/family/all-journals`);
      
      // Separate lesson reflections from personal thoughts
      const lessonReflections = (data || []).filter(e => e.type === "reflection");
      const personalThoughts = (data || []).filter(e => e.type === "journal");
      
      setJournalEntries(lessonReflections);
      setTodaysThoughts(personalThoughts);
    } catch (e) { 
      console.error("Failed to load journal entries:", e);
      // Fallback to old endpoint if new one fails
      try {
        const { data } = await axios.get(`${API}/family/children-work`);
        const reflections = [];
        const thoughts = [];
        
        (data || []).forEach((item) => {
          const childName = item.child?.name || "Child";
          
          // Personal journal entries -> Today's Thoughts
          if (item.journal_entries?.length > 0) {
            item.journal_entries.forEach((entry) => {
              thoughts.push({
                childName,
                type: "journal",
                content: entry.content,
                createdAt: entry.created_at
              });
            });
          }
          
          // Lesson answers -> Journal Entries
          if (item.answers?.length > 0) {
            item.answers.forEach((answer) => {
              const prompt = item.lesson?.reflection_prompts?.[answer.prompt_index] || "";
              reflections.push({
                childName,
                type: "reflection",
                lessonTitle: item.lesson?.title || "",
                prompt,
                content: answer.content,
                createdAt: answer.created_at
              });
            });
          }
        });
        
        reflections.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        thoughts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setJournalEntries(reflections);
        setTodaysThoughts(thoughts);
      } catch (e2) { console.error(e2); }
    }
  };

  const loadFamilyStudyHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/family-study/history`);
      setFamilyStudyHistory(data || []);
    } catch (e) { console.error(e); }
  };
  
  const loadChildrenRewards = async () => {
    try {
      const { data } = await axios.get(`${API}/family/children-rewards`);
      setChildrenRewards(data || []);
    } catch (e) { console.error(e); }
  };
  
  const loadCurriculumWeek = async () => {
    try {
      const { data } = await axios.get(`${API}/curriculum/current-week`);
      setCurriculumWeek(data);
    } catch (e) { console.error("Failed to load curriculum:", e); }
  };
  
  const loadFamilyLessonDay = async () => {
    try {
      const { data } = await axios.get(`${API}/family/lesson-day`);
      setFamilyLessonDay(data);
    } catch (e) { console.error("Failed to load family lesson day:", e); }
  };

  const updateFamilyLessonDay = async (day) => {
    try {
      const { data } = await axios.put(`${API}/family/lesson-day`, { day });
      setFamilyLessonDay(prev => ({
        ...prev,
        family_lesson_day: data.family_lesson_day,
        day_name: data.day_name
      }));
      setShowLessonDayPicker(false);
    } catch (e) { console.error("Failed to update family lesson day:", e); }
  };
  
  useEffect(() => { 
    loadChildren(); 
    loadPrayerRequests();
    loadJournalEntries();
    loadFamilyStudyHistory();
    loadChildrenRewards();
    loadCurriculumWeek();
    loadFamilyLessonDay();
  }, []);

  const handleDelete = async (childId) => {
    if (!window.confirm(t('confirm_remove_child'))) return;
    try {
      await axios.delete(`${API}/children/${childId}`);
      setChildren(c => c.filter(ch => ch.id !== childId));
    } catch { alert(t('could_not_delete')); }
  };

  const handleResetLesson = async (childId, childName) => {
    const confirmMessage = t('confirm_reset_lesson') || `Are you sure you want to reset today's lesson for ${childName}? This will allow them to start the lesson from the beginning.`;
    if (!window.confirm(confirmMessage.replace('{childName}', childName))) return;
    
    try {
      const userDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const { data } = await axios.post(`${API}/lessons/reset/${childId}?user_date=${userDate}`);
      
      if (data.success) {
        // Update local state to reflect the reset
        setChildren(c => c.map(ch => 
          ch.id === childId ? { ...ch, today_completed: false } : ch
        ));
        alert(t('lesson_reset_success') || "Lesson has been reset! The child can now start fresh.");
      }
    } catch (e) {
      console.error('Failed to reset lesson:', e);
      alert(t('lesson_reset_error') || "Could not reset the lesson. Please try again.");
    }
  };

  const totalLessons = children.reduce((s, c) => s + (c.total_lessons || 0), 0);
  const totalStars = children.reduce((s, c) => s + (c.total_stars || 0), 0);
  const todayDone = children.filter(c => c.today_completed).length;
  const statValues = [children.length, `${todayDone}/${children.length}`, totalLessons, totalStars];

  return (
    <div className="min-h-screen" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 shadow-md" style={{ background: "linear-gradient(90deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl p-2">
              <BookOpen className="text-white" size={24} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>Bible Buds</h1>
              <p className="text-xs text-white/60 font-medium">{t('parent_dashboard')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-bold text-white">{user?.name}</p>
              <p className="text-xs text-white/60">{user?.email}</p>
            </div>
            <a href="mailto:support@biblebuds.space"
              className="flex items-center gap-1.5 text-white/70 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all text-sm font-semibold"
              data-testid="contact-support-btn" title={t('contact_support') || 'Contact Support'}>
              <Headphones size={16} />
            </a>
            <button onClick={() => setShowChangePassword(true)}
              className="flex items-center gap-1.5 text-white/70 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all text-sm font-semibold"
              data-testid="change-password-btn" title={t('change_password')}>
              <KeyRound size={16} />
            </button>
            <LanguageToggle variant="compact" />
            <button onClick={logout}
              className="flex items-center gap-1.5 text-white/70 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all text-sm font-semibold"
              data-testid="logout-btn">
              <LogOut size={16} /><span className="hidden sm:inline">{t('logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Banner - Compact */}
        <div className="rounded-3xl p-5 mb-6 shadow-lg" style={{ background: "linear-gradient(135deg, #EF476F 0%, #FFD166 60%, #06D6A0 100%)" }}>
          <h2 className="text-2xl font-black text-white mb-0.5" style={{ fontFamily: "Fredoka, sans-serif", textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
            {t('welcome_back')}, {user?.name?.split(" ")[0]}!
          </h2>
          <p className="text-white/90 font-semibold text-sm">{t('family_doing')}</p>
        </div>

        {/* Stat Cards - Compact Row */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {STAT_CARDS.map((s, i) => (
            <div key={i} className="rounded-2xl p-3 shadow-md text-white border-b-3"
              style={{ background: `linear-gradient(135deg, ${s.gradFrom}, ${s.gradTo})`, borderColor: s.gradTo }}
              data-testid={`stat-${i}`}>
              <s.icon size={18} color="white" strokeWidth={2} className="mb-1.5 opacity-80" />
              <p className="text-2xl font-black leading-none">{statValues[i]}</p>
              <p className="text-[10px] font-bold opacity-80 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Family Bible Study - Full Width Hero Card */}
        <div className="rounded-3xl mb-6 shadow-lg border-b-4 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #9B5DE5, #7B3FD3)", borderColor: "#5E2CA5" }}
          data-testid="family-study-banner">
          <div className="absolute top-0 right-0 w-40 h-40 opacity-10">
            <Users2 size={160} color="white" strokeWidth={1} />
          </div>
          {/* Top Row: Title + Day + Open */}
          <div className="p-5 pb-3 flex items-center gap-4 relative z-10 cursor-pointer hover:opacity-90 transition-all"
            onClick={() => navigate("/family")}>
            <div className="bg-white/20 rounded-2xl p-3">
              <Users2 size={32} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-xl" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('family_bible_study')}</p>
              <p className="text-white/70 text-sm font-medium">{t('gather_family')}</p>
            </div>
            {familyLessonDay && (
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-4 py-3 text-center">
                <p className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Lesson Day</p>
                <p className="text-white font-black text-lg leading-tight" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {familyLessonDay.day_name || DAY_NAMES[familyLessonDay.family_lesson_day || 0]}
                </p>
                <button onClick={(e) => { e.stopPropagation(); setShowLessonDayPicker(true); }}
                  className="text-white/60 text-[10px] font-bold hover:text-white transition-colors mt-0.5">
                  Change
                </button>
              </div>
            )}
            <div className="bg-white text-[#9B5DE5] font-black text-sm px-4 py-2.5 rounded-xl shadow-md flex-shrink-0">
              {t('open') || 'Open'}
            </div>
          </div>
          {/* Video Preview Row */}
          {familyLessonDay?.video && (
            <div className="px-5 pb-4 relative z-10">
              <a href={familyLessonDay.video.youtube_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-3 bg-white/15 hover:bg-white/25 rounded-2xl p-3 transition-all"
                data-testid="family-video-preview">
                <div className="relative w-20 h-12 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={familyLessonDay.video.thumbnail_url} alt={familyLessonDay.video.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = "https://img.youtube.com/vi/fZCY4xmRR1U/hqdefault.jpg"; }} />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <Play size={18} className="text-white" fill="white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{familyLessonDay.video.title}</p>
                  <p className="text-white/60 text-xs">{familyLessonDay.video.duration}</p>
                </div>
                <Video size={18} color="white" className="opacity-50 flex-shrink-0" />
              </a>
            </div>
          )}
        </div>

        {/* Quick Actions Grid - 3 columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {/* Analytics */}
          <div className="rounded-2xl p-4 shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all border-b-4"
            style={{ background: "linear-gradient(135deg, #F77F00, #FFD166)", borderColor: "#d96a00" }}
            onClick={() => navigate("/parent/analytics")} data-testid="analytics-dashboard-btn">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2.5">
              <BarChart3 size={22} color="white" strokeWidth={2.5} />
            </div>
            <p className="text-white font-black text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('analytics')}</p>
            <p className="text-white/70 text-xs font-medium mt-0.5">{t('view_detailed_reports')}</p>
          </div>

          {/* Discussion Cards - links to visible section below */}
          <div className="rounded-2xl p-4 shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all border-b-4"
            style={{ background: "linear-gradient(135deg, #06D6A0, #049a73)", borderColor: "#038a5f" }}
            onClick={() => {
              const el = document.querySelector('[data-testid="discussion-cards-trigger"]');
              if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => el.click(), 400); }
            }}
            data-testid="discussion-cards-box">
            <div className="bg-white/20 rounded-xl p-2 w-fit mb-2.5">
              <MessageCircle size={22} color="white" strokeWidth={2.5} />
            </div>
            <p className="text-white font-black text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>Discussion Cards</p>
            <p className="text-white/70 text-xs font-medium mt-0.5">Dinner table conversation starters</p>
          </div>

          {/* Notifications */}
          {isSupported && (
            <div className="rounded-2xl p-4 shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all border-b-4"
              style={{ 
                background: isSubscribed ? "linear-gradient(135deg, #06D6A0, #049a73)" : "linear-gradient(135deg, #FFD166, #F77F00)",
                borderColor: isSubscribed ? "#038a5f" : "#d96a00"
              }}
              onClick={handleToggleNotifications}
              data-testid="notifications-toggle-btn">
              <div className="bg-white/20 rounded-xl p-2 w-fit mb-2.5">
                {isSubscribed ? <Bell size={22} color="white" strokeWidth={2.5} /> : <BellOff size={22} color="white" strokeWidth={2.5} />}
              </div>
              <p className="text-white font-black text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {isSubscribed ? t('reminders_on') : t('reminders_off')}
              </p>
              <p className="text-white/70 text-xs font-medium mt-0.5">
                {isSubscribed ? t('daily_reminder') : t('tap_enable')}
              </p>
            </div>
          )}
        </div>

        {/* Curriculum Preview */}
        {curriculumWeek && (
          <div className="mb-6">
            <CurriculumPreviewSection curriculum={curriculumWeek} t={t} />
          </div>
        )}

        {/* Discussion Cards - Visible */}
        <div className="mb-6">
          <DiscussionCards />
        </div>
        
        {/* Family Lesson Day Picker Modal */}
        {showLessonDayPicker && (
          <FamilyLessonDayPicker
            currentDay={familyLessonDay?.family_lesson_day || 0}
            onSelect={updateFamilyLessonDay}
            onClose={() => setShowLessonDayPicker(false)}
            t={t}
          />
        )}

        {/* Expandable Sections: Journal, Thoughts, Rewards */}
        <JournalEntriesSection entries={journalEntries} t={t} />
        <TodaysThoughtsSection thoughts={todaysThoughts} t={t} />
        <ChildrenRewardsSection rewards={childrenRewards} t={t} />
        <FamilyDevotionalHistory history={familyStudyHistory} children={children} t={t} language={language} />

        {/* Children Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('your_children')}</h3>
            <button onClick={() => { setEditChild(null); setShowAddModal(true); }}
              className="flex items-center gap-2 font-bold px-5 py-2.5 rounded-xl border-b-2 active:border-b-0 active:translate-y-0.5 transition-all text-sm text-white shadow-md"
              style={{ background: "#06D6A0", borderColor: "#049a73" }}
              data-testid="add-child-btn">
              <Plus size={16} /> {t('add_child')}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-[#118AB2] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500 font-medium">{t('loading')}</p>
              </div>
            </div>
          ) : children.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border-2 border-dashed border-slate-200 shadow-sm">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "linear-gradient(135deg, #118AB2, #06D6A0)" }}>
                <Users size={36} color="white" strokeWidth={2} />
              </div>
              <h3 className="text-xl font-black text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('add_first_child')}</h3>
              <p className="text-slate-500 mb-6">{t('setup_profile')}</p>
              <button onClick={() => { setEditChild(null); setShowAddModal(true); }}
                className="text-white font-bold px-6 py-3 rounded-xl" style={{ background: "#06D6A0" }}
                data-testid="add-first-child-btn">
                {t('add_child')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {children.map(child => (
                <ChildCard key={child.id} child={child}
                  onSelect={() => navigate(`/child/${child.id}`)}
                  onEdit={() => { setEditChild(child); setShowAddModal(true); }}
                  onDelete={() => handleDelete(child.id)}
                  onSetPrize={() => setShowPrizeModal(child.id)}
                  onResetLesson={() => handleResetLesson(child.id, child.name)}
                  t={t} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Share Bible Buds Section */}
      <ShareBibleBudsSection t={t} />

      {/* Subscription Support Message */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            {t('cancel_subscription_message') || 'To cancel your subscription, please reach out to us at'}{' '}
            <a href="mailto:support@biblebuds.space" className="font-bold text-[#118AB2] hover:underline">
              support@biblebuds.space
            </a>
            {' '}{t('cancel_subscription_provide_email') || 'and provide the email used to log into biblebuds.space.'}
          </p>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddChildModal editChild={editChild}
          onClose={() => { setShowAddModal(false); setEditChild(null); }}
          onSaved={loadChildren} />
      )}

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} t={t} />}

      {showPrizeModal && (
        <SetPrizeModal 
          childId={showPrizeModal} 
          childName={children.find(c => c.id === showPrizeModal)?.name}
          onClose={() => setShowPrizeModal(null)}
          t={t}
        />
      )}

      {showPrayerModal && (
        <PrayerRequestModal 
          onClose={() => setShowPrayerModal(false)}
          onSaved={loadPrayerRequests}
          t={t}
        />
      )}
    </div>
  );
}

// Category color mapping for curriculum themes
const CATEGORY_COLORS = {
  "green": { bg: "linear-gradient(135deg, #06D6A0, #049a73)", border: "#049a73", text: "#06D6A0" },
  "blue": { bg: "linear-gradient(135deg, #118AB2, #0d6e91)", border: "#0d6e91", text: "#118AB2" },
  "yellow": { bg: "linear-gradient(135deg, #FFD166, #F77F00)", border: "#d4a749", text: "#F77F00" },
  "red": { bg: "linear-gradient(135deg, #EF476F, #d63c5c)", border: "#d63c5c", text: "#EF476F" },
  "purple": { bg: "linear-gradient(135deg, #9B5DE5, #7a3fc3)", border: "#7a3fc3", text: "#9B5DE5" },
  "orange": { bg: "linear-gradient(135deg, #F77F00, #FFD166)", border: "#d96a00", text: "#F77F00" },
  "brown": { bg: "linear-gradient(135deg, #8B6914, #b38c1d)", border: "#6d5210", text: "#8B6914" },
  "white": { bg: "linear-gradient(135deg, #E0E0E0, #BDBDBD)", border: "#9E9E9E", text: "#757575" },
  "teal": { bg: "linear-gradient(135deg, #20C997, #17a085)", border: "#17a085", text: "#20C997" },
  "lime": { bg: "linear-gradient(135deg, #32CD32, #228B22)", border: "#228B22", text: "#32CD32" },
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_NAMES_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

// Family Lesson Day Section Component
function FamilyLessonDaySection({ data, onChangeDay, t }) {
  const video = data.video;
  
  return (
    <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6" data-testid="family-lesson-day-section">
      {/* Header */}
      <div 
        className="p-4"
        style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl p-2.5">
              <Video size={24} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-white/80 text-xs font-bold uppercase tracking-wide">
                {t('weekly_family_lesson') || "Weekly Family Lesson"}
              </p>
              <h3 className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {data.day_name}
              </h3>
            </div>
          </div>
          <button 
            onClick={onChangeDay}
            className="bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-all"
            data-testid="change-lesson-day-btn"
          >
            <span className="text-white font-bold text-xs">{t('change') || "Change"}</span>
          </button>
        </div>
      </div>
      
      {/* Video Card */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-purple-100 text-purple-600">
            {t('week')} {data.current_week}: {data.curriculum_theme}
          </span>
        </div>
        
        <a 
          href={video.youtube_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] group"
        >
          <img 
            src={video.thumbnail_url} 
            alt={video.title}
            className="w-full h-40 object-cover"
            onError={(e) => { e.target.src = "https://img.youtube.com/vi/fZCY4xmRR1U/hqdefault.jpg"; }}
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-all flex items-center justify-center">
            <div className="bg-white/90 rounded-full p-4 shadow-lg group-hover:scale-110 transition-transform">
              <Play size={32} className="text-purple-600 ml-1" fill="currentColor" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white font-bold text-sm">{video.title}</p>
            <p className="text-white/70 text-xs">{video.duration}</p>
          </div>
        </a>
        
        <p className="text-gray-600 text-xs mt-3 text-center">
          {t('family_lesson_tip') || "Watch this video together as a family on your chosen day!"}
        </p>
      </div>
    </div>
  );
}

// Family Lesson Day Picker Modal
function FamilyLessonDayPicker({ currentDay, onSelect, onClose, t }) {
  const language = t('language_code') || 'en';
  const days = language === 'es' ? DAY_NAMES_ES : DAY_NAMES;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-xl p-2">
                <Calendar size={24} color="white" />
              </div>
              <div>
                <h2 className="text-white font-black text-xl" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('choose_lesson_day') || "Choose Lesson Day"}
                </h2>
                <p className="text-white/80 text-sm">
                  {t('choose_lesson_day_subtitle') || "When does your family watch together?"}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all">
              <X size={24} color="white" />
            </button>
          </div>
        </div>
        
        {/* Day Options */}
        <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
          {days.map((day, index) => (
            <button
              key={day}
              onClick={() => onSelect(index)}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                index === currentDay 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-100 hover:border-purple-200 hover:bg-gray-50'
              }`}
              data-testid={`day-option-${index}`}
            >
              <span className={`font-bold ${index === currentDay ? 'text-purple-600' : 'text-gray-700'}`}>
                {day}
              </span>
              {index === currentDay && (
                <CheckCircle size={20} className="text-purple-500" />
              )}
            </button>
          ))}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-bold text-gray-600 bg-gray-200 hover:bg-gray-300 transition-all"
          >
            {t('cancel') || "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Curriculum Preview Section Component
function CurriculumPreviewSection({ curriculum, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullCurriculum, setShowFullCurriculum] = useState(false);
  const [fullCurriculumData, setFullCurriculumData] = useState(null);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  
  const colors = CATEGORY_COLORS[curriculum.category_color] || CATEGORY_COLORS.purple;
  const progressPercent = (curriculum.week_number / curriculum.total_weeks) * 100;
  
  const loadFullCurriculum = async () => {
    if (fullCurriculumData) {
      setShowFullCurriculum(true);
      return;
    }
    setLoadingCurriculum(true);
    try {
      const { data } = await axios.get(`${API}/curriculum/all`);
      setFullCurriculumData(data);
      setShowFullCurriculum(true);
    } catch (e) {
      console.error("Failed to load full curriculum:", e);
    } finally {
      setLoadingCurriculum(false);
    }
  };
  
  return (
    <>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6" data-testid="curriculum-preview-section">
        {/* Header */}
        <div 
          className="p-4 cursor-pointer hover:opacity-95 transition-all"
          style={{ background: colors.bg }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-xl p-2.5">
                <Calendar size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-white/80 text-xs font-bold uppercase tracking-wide">
                  {t('this_weeks_curriculum') || "This Week's Curriculum"}
                </p>
                <h3 className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {curriculum.theme}
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-lg px-3 py-1.5">
                <span className="text-white font-bold text-sm">
                  {t('week')} {curriculum.week_number}/{curriculum.total_weeks}
                </span>
              </div>
              <ChevronRight 
                size={20} 
                color="white" 
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
              />
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <div className="bg-white/20 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-white h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-white/70 text-xs mt-1 text-right">
              {Math.round(progressPercent)}% {t('curriculum_complete') || "complete"}
            </p>
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="p-4 space-y-4 bg-gradient-to-b from-white to-gray-50">
            {/* Category Badge */}
            <div className="flex items-center gap-2">
              <span 
                className="px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ background: colors.text }}
              >
                {curriculum.category}
              </span>
              <span className="text-gray-500 text-xs">•</span>
              <span className="text-gray-600 text-sm font-medium">
                {curriculum.scripture}
              </span>
            </div>
            
            {/* Key Verse */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border-l-4" style={{ borderColor: colors.text }}>
              <div className="flex items-start gap-2">
                <Sparkles size={18} style={{ color: colors.text }} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-gray-700 mb-1">
                    {t('key_verse') || "Key Verse"}
                  </p>
                  <p className="text-gray-800 font-medium italic">
                    "{curriculum.key_verse}"
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    — {curriculum.verse_reference}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Teaching Focus */}
            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                {t('teaching_focus') || "Teaching Focus"}
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {curriculum.teaching_focus}
              </p>
            </div>
            
            {/* Tip for Parents */}
            <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-100">
              <p className="text-xs text-yellow-800 font-medium">
                <span className="font-bold">💡 {t('parent_tip') || "Parent Tip"}:</span>{' '}
                {t('curriculum_parent_tip') || "This week's lessons will focus on this theme. You can reinforce the message at home through conversation and activities!"}
              </p>
            </div>
            
            {/* View Full Curriculum Button */}
            <button
              onClick={(e) => { e.stopPropagation(); loadFullCurriculum(); }}
              disabled={loadingCurriculum}
              className="w-full py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ background: colors.bg }}
              data-testid="view-full-curriculum-btn"
            >
              {loadingCurriculum ? (
                <span>{t('loading') || "Loading..."}</span>
              ) : (
                <>
                  <BookOpen size={18} />
                  <span>{t('view_full_curriculum') || "View Full 30-Week Curriculum"}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Full Curriculum Modal */}
      {showFullCurriculum && fullCurriculumData && (
        <FullCurriculumModal 
          data={fullCurriculumData} 
          currentWeek={curriculum.week_number}
          onClose={() => setShowFullCurriculum(false)} 
          t={t} 
        />
      )}
    </>
  );
}

// Full Curriculum Modal Component
function FullCurriculumModal({ data, currentWeek, onClose, t }) {
  const [selectedWeek, setSelectedWeek] = useState(null);
  
  // Group curriculum by category
  const groupedCurriculum = data.curriculum.reduce((acc, week) => {
    if (!acc[week.category]) {
      acc[week.category] = {
        weeks: [],
        color: week.category_color
      };
    }
    acc[week.category].weeks.push(week);
    return acc;
  }, {});
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-5 border-b flex items-center justify-between" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl p-2">
              <BookOpen size={24} color="white" />
            </div>
            <div>
              <h2 className="text-white font-black text-xl" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('full_curriculum_title') || "30-Week Bible Journey"}
              </h2>
              <p className="text-white/80 text-sm">
                {t('full_curriculum_subtitle') || "Your family's faith adventure"}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all"
          >
            <X size={24} color="white" />
          </button>
        </div>
        
        {/* Progress Summary */}
        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-700">
              {t('your_progress') || "Your Progress"}
            </span>
            <span className="text-sm font-bold text-purple-600">
              {t('week')} {currentWeek} {t('of') || "of"} 30
            </span>
          </div>
          <div className="bg-white/60 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(currentWeek / 30) * 100}%`,
                background: "linear-gradient(90deg, #9B5DE5, #EF476F)"
              }}
            />
          </div>
        </div>
        
        {/* Curriculum Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {Object.entries(groupedCurriculum).map(([category, { weeks, color }]) => {
            const categoryColors = CATEGORY_COLORS[color] || CATEGORY_COLORS.purple;
            return (
              <div key={category} className="space-y-3">
                {/* Category Header */}
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ background: categoryColors.text }}
                  />
                  <h3 className="font-bold text-gray-800" style={{ fontFamily: "Fredoka, sans-serif" }}>
                    {category}
                  </h3>
                  <span className="text-xs text-gray-400">
                    ({weeks.length} {t('weeks_label') || "weeks"})
                  </span>
                </div>
                
                {/* Weeks Grid */}
                <div className="grid gap-2">
                  {weeks.map((week) => {
                    const isCurrentWeek = week.week === currentWeek;
                    const isCompleted = week.week < currentWeek;
                    const isFuture = week.week > currentWeek;
                    const isSelected = selectedWeek?.week === week.week;
                    
                    return (
                      <div key={week.week}>
                        <div 
                          onClick={() => setSelectedWeek(isSelected ? null : week)}
                          className={`p-3 rounded-xl border transition-all cursor-pointer ${
                            isCurrentWeek 
                              ? 'border-2 shadow-md' 
                              : isCompleted 
                                ? 'border-gray-100 bg-gray-50 hover:bg-gray-100' 
                                : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                          } ${isSelected ? 'ring-2 ring-purple-300' : ''}`}
                          style={isCurrentWeek ? { borderColor: categoryColors.text, background: `${categoryColors.text}10` } : {}}
                        >
                          <div className="flex items-start gap-3">
                            {/* Week Status Icon */}
                            <div className="flex-shrink-0 mt-0.5">
                              {isCompleted ? (
                                <CheckCircle size={18} className="text-green-500" />
                              ) : isCurrentWeek ? (
                                <div 
                                  className="w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center"
                                  style={{ borderColor: categoryColors.text }}
                                >
                                  <div 
                                    className="w-2 h-2 rounded-full animate-pulse"
                                    style={{ background: categoryColors.text }}
                                  />
                                </div>
                              ) : (
                                <Circle size={18} className="text-gray-300" />
                              )}
                            </div>
                            
                            {/* Week Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-bold ${isCurrentWeek ? 'text-purple-600' : 'text-gray-400'}`}>
                                  {t('week')} {week.week}
                                </span>
                                {isCurrentWeek && (
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">
                                    {t('current') || "Current"}
                                  </span>
                                )}
                                {isFuture && (
                                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-500">
                                    {t('upcoming') || "Upcoming"}
                                  </span>
                                )}
                              </div>
                              <h4 className={`font-bold text-sm ${isCompleted ? 'text-gray-500' : 'text-gray-800'}`}>
                                {week.theme}
                              </h4>
                              <p className="text-xs text-gray-500 truncate">
                                {week.scripture} • {week.verse_reference}
                              </p>
                            </div>
                            
                            {/* Expand Icon */}
                            <ChevronRight 
                              size={16} 
                              className={`text-gray-400 transition-transform flex-shrink-0 ${isSelected ? 'rotate-90' : ''}`}
                            />
                          </div>
                        </div>
                        
                        {/* Week Detail Panel */}
                        {isSelected && (
                          <WeekDetailPanel week={week} categoryColors={categoryColors} currentWeek={currentWeek} t={t} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Modal Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}
          >
            {t('close') || "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Week Detail Panel Component
function WeekDetailPanel({ week, categoryColors, currentWeek, t }) {
  const isCompleted = week.week < currentWeek;
  const isCurrent = week.week === currentWeek;
  const isFuture = week.week > currentWeek;
  const weeksUntil = week.week - currentWeek;
  
  return (
    <div 
      className="mt-2 p-4 rounded-xl border-2 space-y-3 animate-in slide-in-from-top-2 duration-200"
      style={{ borderColor: categoryColors.text, background: `${categoryColors.text}08` }}
    >
      {/* Status Badge */}
      <div className="flex items-center gap-2 flex-wrap">
        {isCompleted && (
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle size={12} /> {t('completed') || "Completed"}
          </span>
        )}
        {isCurrent && (
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
            <Sparkles size={12} /> {t('this_week') || "This Week"}
          </span>
        )}
        {isFuture && (
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
            <Calendar size={12} /> {weeksUntil === 1 ? (t('next_week') || "Next Week") : `${t('in') || "In"} ${weeksUntil} ${t('weeks_label') || "weeks"}`}
          </span>
        )}
        <span 
          className="text-xs font-bold px-3 py-1 rounded-full text-white"
          style={{ background: categoryColors.text }}
        >
          {week.category}
        </span>
      </div>
      
      {/* Theme & Scripture */}
      <div>
        <h4 className="font-black text-lg text-gray-800" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {week.theme}
        </h4>
        <p className="text-sm text-gray-600 font-medium">
          {t('scripture_reading') || "Scripture"}: {week.scripture}
        </p>
      </div>
      
      {/* Key Verse */}
      <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
        <div className="flex items-start gap-2">
          <Sparkles size={16} style={{ color: categoryColors.text }} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">
              {t('key_verse') || "Key Verse"}
            </p>
            <p className="text-gray-800 font-medium italic text-sm">
              "{week.key_verse}"
            </p>
            <p className="text-xs text-gray-500 mt-1">
              — {week.verse_reference}
            </p>
          </div>
        </div>
      </div>
      
      {/* Teaching Focus */}
      <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
        <p className="text-xs font-bold text-gray-500 uppercase mb-1">
          {t('teaching_focus') || "Teaching Focus"}
        </p>
        <p className="text-gray-700 text-sm leading-relaxed">
          {week.teaching_focus}
        </p>
      </div>
      
      {/* Parent Preparation Tip for Future Weeks */}
      {isFuture && (
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
          <p className="text-xs text-blue-800 font-medium">
            <span className="font-bold">📚 {t('prepare_ahead') || "Prepare Ahead"}:</span>{' '}
            {t('prepare_ahead_tip') || "Read through the scripture this week so you're ready to discuss it with your children when this lesson arrives!"}
          </p>
        </div>
      )}
      
      {/* Reflection for Completed Weeks */}
      {isCompleted && (
        <div className="bg-green-50 rounded-xl p-3 border border-green-100">
          <p className="text-xs text-green-800 font-medium">
            <span className="font-bold">✨ {t('great_job') || "Great Job"}:</span>{' '}
            {t('completed_week_tip') || "Your family has already learned about this topic! Consider reviewing the key verse together to reinforce the message."}
          </p>
        </div>
      )}
    </div>
  );
}

// Journal Entries Section Component (for lesson reflections) - Grouped by Child
function JournalEntriesSection({ entries, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState({});
  
  // Group entries by child name
  const entriesByChild = entries.reduce((acc, entry) => {
    const childName = entry.childName || "Unknown";
    if (!acc[childName]) {
      acc[childName] = [];
    }
    acc[childName].push(entry);
    return acc;
  }, {});
  
  const childNames = Object.keys(entriesByChild);
  
  const toggleChild = (childName) => {
    setExpandedChildren(prev => ({
      ...prev,
      [childName]: !prev[childName]
    }));
  };
  
  return (
    <div className="mb-4">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#118AB2" }}>
        <div 
          className="p-4 cursor-pointer hover:opacity-95 transition-opacity" 
          style={{ background: "linear-gradient(135deg, #118AB2, #06D6A0)" }}
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="journal-section-header"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen size={20} color="white" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('journal_entries') || "Journal Entries"}</h3>
                <p className="text-white/80 text-xs mt-0.5">{t('lesson_reflections') || "Answers from lesson reflection prompts"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {entries.length > 0 && (
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                </span>
              )}
              <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-4">
            {childNames.length > 0 ? (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {childNames.map((childName) => {
                  const childEntries = entriesByChild[childName];
                  const isChildExpanded = expandedChildren[childName];
                  const colors = ["#118AB2", "#9B5DE5", "#EF476F", "#06D6A0", "#F77F00"];
                  const colorIndex = childName.charCodeAt(0) % colors.length;
                  const childColor = colors[colorIndex];
                  
                  return (
                    <div key={childName} className="rounded-2xl overflow-hidden border-2" style={{ borderColor: childColor + "40" }}>
                      {/* Child Header - Clickable */}
                      <div 
                        className="p-3 cursor-pointer flex items-center justify-between"
                        style={{ background: childColor + "15" }}
                        onClick={() => toggleChild(childName)}
                        data-testid={`journal-child-${childName}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-lg"
                            style={{ background: childColor }}>
                            {childName[0]}
                          </div>
                          <div>
                            <p className="font-black text-[#073B4C]">{childName}</p>
                            <p className="text-xs text-gray-500">{childEntries.length} {childEntries.length === 1 ? t('entry') || 'entry' : t('entries') || 'entries'}</p>
                          </div>
                        </div>
                        <div className={`transform transition-transform duration-200 ${isChildExpanded ? 'rotate-180' : ''}`}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={childColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </div>
                      
                      {/* Child's Entries - Expandable */}
                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isChildExpanded ? 'max-h-[400px]' : 'max-h-0'}`}>
                        <div className="p-3 space-y-2 bg-white max-h-[380px] overflow-y-auto">
                          {childEntries.slice(0, 10).map((entry, i) => (
                            <div key={i} className="rounded-xl p-3 border-l-4"
                              style={{ 
                                background: "#F8FAFC",
                                borderColor: childColor
                              }}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs text-gray-500">
                                  {entry.type === "reflection" ? `${t('lesson')}: ${entry.lessonTitle}` : 
                                   entry.type === "journal" ? t('personal_journal') : `${t('activity')}: ${entry.taskTitle}`}
                                </p>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                                  style={{ background: childColor }}>
                                  {entry.type === "reflection" ? t('reflection') : entry.type === "journal" ? t('journal') : t('activity')}
                                </span>
                              </div>
                              
                              {entry.prompt && (
                                <p className="text-xs text-gray-500 italic mb-1">{t('question_prefix')} {entry.prompt}</p>
                              )}
                              
                              <div className="bg-white rounded-lg p-2 border">
                                <p className="text-sm text-gray-700" style={{ fontFamily: "Quicksand, sans-serif" }}>"{entry.content}"</p>
                              </div>
                              
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(entry.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <PenLine size={28} className="text-gray-400" />
                </div>
                <p className="font-bold text-gray-500">{t('no_journal_entries')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('reflections_appear_here')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Today's Thoughts Section Component (personal reflections from kids) - Grouped by Child
function TodaysThoughtsSection({ thoughts, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState({});
  
  // Group thoughts by child name
  const thoughtsByChild = thoughts.reduce((acc, thought) => {
    const childName = thought.childName || "Unknown";
    if (!acc[childName]) {
      acc[childName] = [];
    }
    acc[childName].push(thought);
    return acc;
  }, {});
  
  const childNames = Object.keys(thoughtsByChild);
  
  const toggleChild = (childName) => {
    setExpandedChildren(prev => ({
      ...prev,
      [childName]: !prev[childName]
    }));
  };
  
  return (
    <div className="mb-4">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
        <div 
          className="p-4 cursor-pointer hover:opacity-95 transition-opacity" 
          style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="thoughts-section-header"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PenLine size={20} color="white" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>{t('todays_thoughts') || "Today's Thoughts"}</h3>
                <p className="text-white/80 text-xs mt-0.5">{t('personal_thoughts_desc') || "Personal thoughts your children wrote"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {thoughts.length > 0 && (
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {thoughts.length} {thoughts.length === 1 ? 'thought' : 'thoughts'}
                </span>
              )}
              <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-4">
            {childNames.length > 0 ? (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {childNames.map((childName) => {
                  const childThoughts = thoughtsByChild[childName];
                  const isChildExpanded = expandedChildren[childName];
                  const colors = ["#9B5DE5", "#EF476F", "#F77F00", "#118AB2", "#06D6A0"];
                  const colorIndex = childName.charCodeAt(0) % colors.length;
                  const childColor = colors[colorIndex];
                  
                  return (
                    <div key={childName} className="rounded-2xl overflow-hidden border-2" style={{ borderColor: childColor + "40" }}>
                      {/* Child Header - Clickable */}
                      <div 
                        className="p-3 cursor-pointer flex items-center justify-between"
                        style={{ background: childColor + "15" }}
                        onClick={() => toggleChild(childName)}
                        data-testid={`thoughts-child-${childName}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-lg"
                            style={{ background: childColor }}>
                            {childName[0]}
                          </div>
                          <div>
                            <p className="font-black text-[#073B4C]">{childName}</p>
                            <p className="text-xs text-gray-500">{childThoughts.length} {childThoughts.length === 1 ? t('thought') || 'thought' : t('thoughts') || 'thoughts'}</p>
                          </div>
                        </div>
                        <div className={`transform transition-transform duration-200 ${isChildExpanded ? 'rotate-180' : ''}`}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={childColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </div>
                      
                      {/* Child's Thoughts - Expandable */}
                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isChildExpanded ? 'max-h-[400px]' : 'max-h-0'}`}>
                        <div className="p-3 space-y-2 bg-white max-h-[380px] overflow-y-auto">
                          {childThoughts.slice(0, 10).map((thought, i) => (
                            <div key={i} className="rounded-xl p-3 border-l-4"
                              style={{ 
                                background: "#F8FAFC",
                                borderColor: childColor
                              }}>
                              <div className="bg-white rounded-lg p-2 border">
                                <p className="text-sm text-gray-700" style={{ fontFamily: "Quicksand, sans-serif" }}>"{thought.content}"</p>
                              </div>
                              
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(thought.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {new Date(thought.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <PenLine size={28} className="text-purple-400" />
                </div>
                <p className="font-bold text-gray-500">{t('no_thoughts_yet') || "No thoughts yet"}</p>
                <p className="text-xs text-gray-400 mt-1">{t('thoughts_appear_here') || "Children's personal thoughts will appear here"}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Share Bible Buds Section Component - Flyer and Video
function ShareBibleBudsSection({ t }) {
  const shareToSocial = (platform) => {
    const shareText = t('share_promo_text') || "Check out Bible Buds! Daily Bible lessons, fun activities, and family study time for kids ages 3-18. Our family loves it! 🙏✨";
    const shareUrl = "https://biblebuds.space";
    const hashtags = "BibleBuds,ChristianParenting,KidsFaith,FamilyFaith";
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent("Check out Bible Buds!")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`,
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Bible Buds - Daily Bible Lessons for Kids',
          text: t('share_promo_text') || "Check out Bible Buds! Daily Bible lessons, fun activities, and family study time for kids ages 3-18.",
          url: 'https://biblebuds.space'
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 pb-8">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
        <div className="p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center gap-2">
            <Share2 size={20} color="white" />
            <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('share_bible_buds_heading') || 'Share Bible Buds & Help It Go Viral!'}
            </h3>
          </div>
          <p className="text-white/80 text-xs mt-1">{t('share_bible_buds_desc') || 'Share with friends, family, and your church community!'}</p>
        </div>
        
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Video Section */}
            <div>
              <p className="text-xs font-bold text-[#118AB2] uppercase tracking-wide mb-2">{t('promo_video') || 'Promo Video'}</p>
              <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#118AB2]/20">
                <video controls className="w-full"
                  poster="/images/biblebuds-flyer.png"
                  data-testid="dashboard-promo-video">
                  <source src="/bible-buds-promo.mp4" type="video/mp4" />
                  {t('video_not_supported') || 'Your browser does not support the video tag.'}
                </video>
              </div>
              <a href="/bible-buds-promo.mp4" download="BibleBuds-Promo.mp4"
                className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-[#118AB2] to-[#06D6A0] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 transition-all text-sm w-full justify-center"
                data-testid="download-video-btn">
                <DownloadIcon /> {t('download_video') || 'Download Video'}
              </a>
            </div>
            
            {/* Flyer Section */}
            <div>
              <p className="text-xs font-bold text-[#F77F00] uppercase tracking-wide mb-2">{t('promotional_flyer') || 'Promotional Flyer'}</p>
              <div className="flex justify-center">
                <a href="https://biblebuds.space" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="/images/biblebuds-flyer.png"
                    alt={t('flyer_alt') || "Bible Buds - Daily Bible Lessons for Kids"}
                    className="w-48 h-auto object-contain rounded-2xl shadow-lg border-2 border-[#F77F00]/20 hover:shadow-xl transition-shadow cursor-pointer"
                    data-testid="dashboard-flyer-image"
                  />
                </a>
              </div>
              <a href="/images/biblebuds-flyer.png"
                download="BibleBuds-Flyer.png"
                className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-[#F77F00] to-[#FFD166] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 transition-all text-sm w-full justify-center"
                data-testid="download-flyer-btn">
                <DownloadIcon /> {t('download_flyer') || 'Download Flyer'}
              </a>
            </div>
          </div>
          
          {/* Social Share Buttons */}
          <div className="mt-6 pt-4 border-t">
            <p className="text-sm font-bold text-[#073B4C] mb-2 text-center">{t('share_on') || 'Share on:'}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => shareToSocial('facebook')}
                className="flex items-center gap-1 bg-[#1877F2] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 text-sm"
                data-testid="share-facebook">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
              <button onClick={() => shareToSocial('twitter')}
                className="flex items-center gap-1 bg-black text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 text-sm"
                data-testid="share-twitter">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X / Twitter
              </button>
              <button onClick={() => shareToSocial('whatsapp')}
                className="flex items-center gap-1 bg-[#25D366] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 text-sm"
                data-testid="share-whatsapp">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
              <button onClick={() => shareToSocial('email')}
                className="flex items-center gap-1 bg-gray-600 text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 text-sm"
                data-testid="share-email">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                Email
              </button>
              {navigator.share && (
                <button onClick={handleNativeShare}
                  className="flex items-center gap-1 bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 text-sm"
                  data-testid="share-native">
                  <Share2 size={16} />
                  More...
                </button>
              )}
            </div>
          </div>
          
          {/* Tips */}
          <div className="mt-4 bg-gradient-to-r from-[#FFD166]/20 to-[#F77F00]/20 rounded-xl p-3">
            <p className="text-xs font-bold text-[#F77F00] mb-1">💡 {t('viral_tips_title') || 'Tips to Help Bible Buds Go Viral:'}</p>
            <ul className="text-xs text-gray-600 grid grid-cols-2 gap-x-4 gap-y-0.5">
              <li>• {t('viral_tip_1') || 'Share in church Facebook groups'}</li>
              <li>• {t('viral_tip_2') || 'Post on Christian parenting forums'}</li>
              <li>• {t('viral_tip_3') || 'Send to homeschool communities'}</li>
              <li>• {t('viral_tip_4') || 'Text the flyer to 5 friends today!'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple Download Icon
function DownloadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

// Children's Rewards Section Component (Expandable)
function ChildrenRewardsSection({ rewards, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="max-w-6xl mx-auto px-4 pb-4">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#FFD166" }}>
        {/* Header - Clickable to expand */}
        <div 
          className="p-4 cursor-pointer"
          style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)" }}
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="rewards-section-header"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift size={20} color="white" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('childrens_rewards') || "Children's Rewards"}
                </h3>
                <p className="text-white/80 text-xs mt-0.5">
                  {t('track_rewards_progress') || 'Track weekly goals and earned rewards'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {rewards.length > 0 && (
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {rewards.length} {rewards.length === 1 ? 'reward' : 'rewards'}
                </span>
              )}
              <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content - Expandable */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-4">
            {rewards.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {rewards.slice(0, 20).map((reward, i) => (
                  <div key={i} className={`rounded-2xl p-4 border-2 ${
                    reward.claimed 
                      ? "bg-green-50 border-green-300" 
                      : "bg-yellow-50 border-yellow-200"
                  }`}>
                    <div className="flex items-center gap-3">
                      {/* Child Avatar */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm"
                        style={{ background: reward.claimed ? "#06D6A0" : "#FFD166" }}>
                        {(reward.childName || "?")?.[0]}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-black text-sm text-[#073B4C]">{reward.childName}</p>
                          {reward.claimed ? (
                            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                              ✓ {t('claimed') || 'Claimed'}
                            </span>
                          ) : (
                            <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                              {t('in_progress') || 'In Progress'}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm font-bold text-[#F77F00]">{reward.rewardName}</p>
                        {reward.rewardDescription && (
                          <p className="text-xs text-gray-600 mt-1">{reward.rewardDescription}</p>
                        )}
                        
                        {/* Progress bar */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{t('lessons_completed') || 'Lessons completed'}</span>
                            <span className="font-bold">{reward.lessonsCompleted || 0}/{reward.lessonsGoal || 7}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${Math.min(100, ((reward.lessonsCompleted || 0) / (reward.lessonsGoal || 7)) * 100)}%`,
                                background: reward.claimed ? "#06D6A0" : "linear-gradient(90deg, #FFD166, #F77F00)"
                              }}
                            />
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-400 mt-2">
                          {t('week_of') || 'Week of'} {reward.weekStart}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                  <Gift size={28} className="text-yellow-500" />
                </div>
                <p className="font-bold text-gray-500">{t('no_rewards_yet') || 'No rewards set yet'}</p>
                <p className="text-xs text-gray-400 mt-1">{t('set_rewards_hint') || 'Set weekly goals in each child\'s profile'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
